/**
 * 교육 화면(TbmEducateView)의 "작업 전 안전조치 확인" 기준 목록 도출 — 순수 함수.
 *
 * 이 목록은 **점검할 항목**을 깔아 줄 뿐이고, 조치 여부(`checked`)는 교육담당자가 교육 중에
 * 직접 체크해야 한다. 그래서 기본값은 **미선택(`checked` 없음)** 이다 — `false`가 아니다.
 * `false`는 "확인해 보니 조치가 안 됐다"는 **기록**이라, 아무도 안 본 항목에 넣으면 그것도 거짓이다.
 * 미선택이 남은 채로는 교육을 완료할 수 없다(프론트·서버 양쪽 차단).
 *
 * ⚠️ 2026-08-31까지는 여기(와 작성 경로)가 `checked: true`로 채웠다. 그 결과 아무도 표를
 * 건드리지 않은 문서가 "전 항목 조치 완료(예 ☑)"로 인쇄됐고, prod 실측에서 `safetyChecks`를
 * 가진 37건이 **전부 all-true, "아니오" 0건, 조치 내용 0건**이었다 — 이 표가 실제 점검 기록으로
 * 기능한 적이 없다는 뜻이다. 값이 없을 때 지어내지 않는 것(TbmFormRenderer 폴백 제거)과 짝이다.
 *
 * 저장값(`content.safetyChecks`)이 있으면 **항목 이름만** 승계한다 — 기준 목록은 "무엇을
 * 점검하는가"의 목록이고, 그 시점의 체크 상태를 되살리는 자리가 아니다(저장값 복원은 호출부의
 * 별도 경로가 담당한다).
 *
 * ⚠️ 입력은 `tbm.content`가 아니라 **문서 파생**(`resolveTbmEducationDoc`)이다. COMPOSED 문서는
 * 위험요인이 `content['tbmHazards-src']`에 있어 `content.hazardItems`를 직접 읽으면 **빈 목록**이
 * 나오고, 그러면 `findUnselectedSafetyChecks`가 0을 돌려줘 **미선택 차단이 발동하지 않는다** —
 * 표가 빈 채로 교육이 완료된다. 값이 없을 때 지어내지 않는 규약의 반대편 구멍이라, 읽기 축을
 * 파생 하나로 모아 닫는다(결정 `docs/decisions/2026-09-08-composed-education-runtime.md`).
 */
import type { TBMSafetyCheck } from '../../lib/tbm';
import type { TbmEducationDoc } from '../../lib/tbmEducationDoc';

export function deriveSafetyCheckBase(doc: TbmEducationDoc | undefined): TBMSafetyCheck[] {
  // 구 문서가 content 축에 저장해 둔 목록(COMPOSED는 세션 축이라 늘 없다).
  const fromChecks = doc?.savedSafetyChecks;
  if (fromChecks && fromChecks.length > 0) {
    return fromChecks.map((c) => ({ hazard: c.hazard, actionRequired: '' }));
  }
  return (doc?.hazardItems || [])
    .map((h) => h.hazard)
    .filter((h): h is string => !!h)
    .map((hazard) => ({ hazard, actionRequired: '' }));
}

/**
 * 미선택(3상태의 `undefined`) 항목의 위험요인 이름 — 교육 완료 차단의 프론트 판정.
 *
 * "예/아니오 중 무엇도 고르지 않은 것"만 센다. **'아니오'는 막지 않는다** — 조치를 못 했으면
 * 아니오를 고르고 조치 내용을 적는 게 정상 경로고, 그걸 막으면 사용자는 사실이 아닌 '예'를
 * 고르게 된다. 서버도 같은 판정을 하지만(우회 차단) 판정 코드는 각 층이 따로 갖는다.
 */
export function findUnselectedSafetyChecks(
  checks: readonly TBMSafetyCheck[] | undefined | null,
): string[] {
  return (checks ?? [])
    .filter(isUnselectedSafetyCheck)
    .map((c) => c.hazard?.trim() || '(이름 없는 항목)');
}

/** 미선택 판정 — 위 목록과 아래 인덱스가 **한 술어를 공유**한다(두 벌이 되면 갈린다). */
function isUnselectedSafetyCheck(check: TBMSafetyCheck): boolean {
  return typeof check.checked !== 'boolean';
}

/**
 * 첫 미선택 항목의 **인덱스**(없으면 -1) — 차단 시 그 행으로 데려가는 자리가 쓴다.
 *
 * 이름 목록(`findUnselectedSafetyChecks`)으로는 행을 특정할 수 없다: 같은 위험요인 이름이 두 번
 * 나올 수 있고, 이름은 화면 여러 곳에 있어 텍스트로 DOM을 뒤지면 목록이 바뀔 때 조용히 빗나간다.
 */
export function findFirstUnselectedSafetyCheckIndex(
  checks: readonly TBMSafetyCheck[] | undefined | null,
): number {
  return (checks ?? []).findIndex(isUnselectedSafetyCheck);
}
